import React, { useMemo } from 'react'

import { LinkOutlined, LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import { useExport } from '@hooks/shared/useExport'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { SHIPMENT_STATUS_COLORS } from '@shared/shipments/statusColors'
import { useShipmentStatusLabel } from '@constants/shipment'
import { Tag, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface ShipmentService extends TableService {
  export: (ids: unknown, options: { locale: string }) => Promise<unknown>;
}

interface MerchantOption {
  id: string;
  name: string;
}

interface ShippingTypeOption {
  id: string;
  name: string;
}

interface ShipmentRow {
  id: string;
  merchant_name?: string;
  order_id?: string;
  order_name?: string;
  fulfillment_id?: string;
  fulfillment_name?: string;
  return_order_id?: string;
  return_order_name?: string;
  tracking_number?: string;
  barcode?: string;
  status?: string;
  label_status?: string;
  shipping_type?: { name?: string } | null;
  note?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

interface ShipmentsListProps {
  service: ShipmentService; // New prop
  role?: string;
  shippingTypes?: ShippingTypeOption[];
  addJob?: (jobId: string, jobName: string) => void;
}

/**
 * 通用托運單列表組件
 *
 * @param {Object} props
 * @param {string} props.role - 角色 (例如：'admin' 或 'operator')
 * @param {Function} props.addJob - 背景任務管理器的 addJob 函數
 */
const ShipmentsList: React.FC<ShipmentsListProps> = ({
  service, // New prop
  role = 'admin',
  shippingTypes = [],
  addJob
}) => {
  const { t, i18n } = useTranslation()
  const shipmentStatusLabel = useShipmentStatusLabel()
  // Always call hooks unconditionally
  const operatorData = useOperatorData() as { merchants?: MerchantOption[] } | null
  const merchants: MerchantOption[] = role === 'operator' ? (operatorData?.merchants || []) : []
  const shippingTypeOptions = useMemo(
    () => shippingTypes.map(st => ({ label: st.name, value: st.id })),
    [shippingTypes]
  )

  // 匯出功能（使用 Context 共享背景任務管理器）
  const handleExport = (useExport as any)({
    action: service ? (ids: unknown) => service.export(ids, { locale: i18n.language }) : undefined,
    endpoint: 'shipments/export',
    addJob,
    jobName: 'shared.shipments.list.export.job_name',
    i18nKeys: {
      noSelection: 'shared.shipments.list.export.no_selection',
      success: 'common.message.export_success',
      error: 'common.error.export_failed',
    }
  }) as (selected: any) => void

  const columns = useMemo(() => [
    // Operator 專用：merchant_name 欄位
    ...(role === 'operator' ? [{
      nameKey: `models.merchant.name`,
      persist: true,
      sortable: true,
      sortFieldName: 'merchants.name',
      width: '150px',
      wrap: true,
      selector: ({ merchant_name }: ShipmentRow) => merchant_name || '-',
    }] : []),
    {
      nameKey: `models.order.order_number`,
      persist: true,
      sortable: false,
      width: '150px',
      wrap: true,
      cell: ({ order_id, order_name }: ShipmentRow) => {
        if (!order_id || !order_name) return '-'
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/orders/${order_id}`} style={{ wordBreak: 'break-word' }}>
              {order_name}
            </Link>
            <a
              href={`/${role}/orders/${order_id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        )
      },
    },
    {
      nameKey: `models.fulfillment.name`,
      persist: true,
      sortable: false,
      width: '150px',
      wrap: true,
      cell: ({ fulfillment_id, fulfillment_name }: ShipmentRow) => {
        if (!fulfillment_id || !fulfillment_name) return '-'
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/fulfillments/${fulfillment_id}`} style={{ wordBreak: 'break-word' }}>
              {fulfillment_name}
            </Link>
            <a
              href={`/${role}/fulfillments/${fulfillment_id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        )
      },
    },
    {
      nameKey: `models.return_order.name`,
      sortable: false,
      width: '150px',
      wrap: true,
      cell: ({ return_order_id, return_order_name }: ShipmentRow) => {
        if (!return_order_id || !return_order_name) return '-'
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/return_orders/${return_order_id}`} style={{ wordBreak: 'break-word' }}>
              {return_order_name}
            </Link>
            <a
              href={`/${role}/return_orders/${return_order_id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        )
      },
    },
    {
      name: (
        <Space size={4}>
          {t('models.shipment.tracking_number')}
          <Tooltip title={t('shared.shipments.list.column_tooltips.tracking_number')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: true,
      sortFieldName: 'shipments.tracking_number',
      wrap: true,
      selector: ({ tracking_number }: ShipmentRow) => tracking_number,
    },
    {
      name: (
        <Space size={4}>
          {t('models.shipment.barcode')}
          <Tooltip title={t('shared.shipments.list.column_tooltips.barcode')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: true,
      sortFieldName: 'shipments.barcode',
      wrap: true,
      selector: ({ barcode }: ShipmentRow) => barcode,
    },
    {
      name: (
        <Space size={4}>
          {t('models.shipment.status')}
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.shipments.list.column_tooltips.status')}</span>}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      width: '120px',
      wrap: true,
      cell: ({ status }: ShipmentRow) => {
        return <Tag color={(status && SHIPMENT_STATUS_COLORS[status]) || 'default'}>{shipmentStatusLabel(status)}</Tag>
      },
    },
    {
      name: (
        <Space size={4}>
          {t('models.shipment.label_status')}
        </Space>
      ),
      width: '120px',
      wrap: true,
      cell: ({ label_status }: ShipmentRow) => {
        if (!label_status) return '-'
        // 與 PickingWaves/Show.jsx 及 PickingWaves/Show.jsx 的 fulfillment label_status tag 對齊（gold + spin for processing）
        const colors: Record<string, string> = {
          'pending': 'default',
          'processing': 'gold',
          'generated': 'blue',
          'error': 'red',
        }
        return (
          <Tag color={colors[label_status] || 'default'} icon={label_status === 'processing' ? <LoadingOutlined spin /> : null}>
            {t(`enums.shipment.label_status.${label_status}`)}
          </Tag>
        )
      },
    },
    {
      name: (
        <Space size={4}>
          {t('models.order.shipping_type')}
          <Tooltip title={t('shared.shipments.list.column_tooltips.shipping_type')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      width: '150px',
      wrap: true,
      cell: ({ shipping_type }: ShipmentRow) => {
        if (!shipping_type) return '-'
        // 翻譯直接使用 name
        return shipping_type.name
      },
    },
    {
      nameKey: `common.created_at`,
      sortable: true,
      sortFieldName: 'shipments.created_at',
      wrap: true,
      selector: ({ created_at }: ShipmentRow) => created_at,
    },
    {
      nameKey: `common.updated_at`,
      sortable: true,
      sortFieldName: 'shipments.updated_at',
      wrap: true,
      selector: ({ updated_at }: ShipmentRow) => updated_at,
    }
  ], [role, t])
  const supportRules = useMemo<SupportRule[]>(() => [
    // Operator 專用：merchant_id 篩選器
    ...(role === 'operator' ? [{
      label: t(`models.inventory.merchant_id`),
      component: MultipleValue,
      inputName: 'merchants.id',
      defaultPinned: true,
      createable: false,
      defaultOptions: merchants.map((merchant) => ({
        label: merchant.name,
        value: merchant.id,
      })),
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.merchant.title'),
        content: t('shared.shipments.list.filter_tooltips.merchant.content'),
      },
    }] : []),
    {
      label: t(`models.shipment.tracking_number`),
      component: MultipleValue,
      inputName: 'shipments.tracking_number',
      defaultPinned: true,
      createable: true,
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.tracking_number.title'),
        content: t('shared.shipments.list.filter_tooltips.tracking_number.content'),
      },
    },
    {
      label: t(`models.shipment.barcode`),
      component: MultipleValue,
      inputName: 'shipments.barcode',
      createable: true,
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.barcode.title'),
        content: t('shared.shipments.list.filter_tooltips.barcode.content'),
      },
    },
    {
      label: t(`models.order.order_number`),
      component: MultipleValue,
      inputName: 'orders.name',
      createable: true,
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.order_number.title'),
        content: t('shared.shipments.list.filter_tooltips.order_number.content'),
      },
    },
    {
      label: t(`models.fulfillment.name`),
      component: MultipleValue,
      inputName: 'fulfillments.name',
      createable: true,
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.fulfillment_name.title'),
        content: t('shared.shipments.list.filter_tooltips.fulfillment_name.content'),
      },
    },
    {
      label: t(`models.return_order.name`),
      component: MultipleValue,
      inputName: 'return_orders.name',
      createable: true,
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.return_order_name.title'),
        content: t('shared.shipments.list.filter_tooltips.return_order_name.content'),
      },
    },
    {
      label: t(`models.shipment.status`),
      component: MultipleValue,
      inputName: 'shipments.status',
      createable: false,
      defaultOptions: [
        'waiting',
        'shipped',
        'in_transit',
        'arrived_at_hub',
        'arrived_at_destination',
        'received',
        'returned',
        'returned_to_sender',
        'problem',
      ].map((value) => ({ label: t(`enums.shipment.status.${value}`), value })),
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.status.title'),
        content: t('shared.shipments.list.filter_tooltips.status.content'),
      },
    },
    {
      label: t(`models.order.shipping_type`),
      component: MultipleValue,
      inputName: 'shipments.shipping_type_id',
      createable: false,
      defaultOptions: shippingTypeOptions,
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.shipping_type.title'),
        content: t('shared.shipments.list.filter_tooltips.shipping_type.content'),
      },
    },
    {
      label: t(`common.created_at`),
      component: FullDayRangePicker,

      defaultPinned: true,
      sortable: false,
      inputName: 'shipments.created_at',
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.created_at.title'),
        content: t('shared.shipments.list.filter_tooltips.created_at.content'),
      },
    },
    {
      label: t(`common.updated_at`),
      component: FullDayRangePicker,

      sortable: false,
      inputName: 'shipments.updated_at',
      tooltip: {
        title: t('shared.shipments.list.filter_tooltips.updated_at.title'),
        content: t('shared.shipments.list.filter_tooltips.updated_at.content'),
      },
    }
  ], [t, merchants, role, shippingTypeOptions])

  // 操作按鈕定義
  const actions = useMemo(() => ([
    {
      name: `common.button.export`,
      className: 'btn btn-primary btn-outline mb-0',
      do: handleExport
    }
  ]), [handleExport])

  return (
    <TableIndex
      columns={columns}
      searchable={{ match: 'prefix', searchFields: [t('models.shipment.tracking_number'), t('models.shipment.barcode'), t('models.order.order_number'), t('models.return_order.name'), t('models.fulfillment.name')] }}
      columnsEditable={true}
      tableId={`${role}ShipmentsTable`}
      defaultQuery={{
        sorting_field: 'shipments.id',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      actions={actions}
      service={service}
      filterHelpExample={t('shared.shipments.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/shipments/list#features`}
    />
  )
}

export default ShipmentsList
